'use client';

import React, { useState, useEffect, useRef } from 'react';

interface TrickAnnouncerProps {
  trickName: string | null;
  trickPoints: number;
  comboMultiplier: number;
}

interface AnnouncerEntry {
  id: string;
  name: string;
  points: number;
  multiplier: number;
}

export default function TrickAnnouncer({
  trickName,
  trickPoints,
  comboMultiplier,
}: TrickAnnouncerProps) {
  const [entries, setEntries] = useState<AnnouncerEntry[]>([]);
  const counterRef = useRef(0);

  useEffect(() => {
    if (!trickName) return;
    counterRef.current += 1;
    const id = `trick-${counterRef.current}`;
    const entry: AnnouncerEntry = {
      id,
      name: trickName,
      points: trickPoints,
      multiplier: comboMultiplier,
    };
    setEntries(prev => [...prev.slice(-2), entry]);
    const t = setTimeout(() => {
      setEntries(prev => prev.filter(e => e.id !== id));
    }, 900);
    return () => clearTimeout(t);
  }, [trickName, trickPoints, comboMultiplier]);

  if (entries.length === 0) return null;

  return (
    <div className="flex flex-col items-center gap-2 pointer-events-none">
      {entries.map(entry => (
        <div key={entry.id} className="flex flex-col items-center trick-text-glow">
          <p
            className="font-bold text-2xl tracking-wide leading-none"
            style={{
              fontFamily: 'DM Sans, sans-serif',
              color: entry.multiplier >= 4 ? '#ff4d6d' : entry.multiplier >= 2 ? '#ffd60a' : '#00e5ff',
              textShadow: `0 0 20px currentColor, 0 0 40px currentColor`,
              WebkitTextStroke: '1px rgba(0,0,0,0.4)',
            }}
          >
            {entry.name.toUpperCase()}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className="font-mono-game text-base font-bold"
              style={{ color: '#ffd60a' }}
            >
              +{entry.points.toLocaleString()}
            </span>
            {entry.multiplier > 1 && (
              <span
                className="font-mono-game text-xs font-bold px-2 py-0.5 rounded-full"
                style={{
                  background: 'rgba(255,214,10,0.15)',
                  color: '#ffd60a',
                  border: '1px solid rgba(255,214,10,0.3)',
                }}
              >
                x{entry.multiplier}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}